<template>
  <div class="saved-collections">
    <div class="saved-collections__header">
      <saved-collections-back-to-search
        @on-back-to-search-click="handleRedirectToSearchPage"
      >
      </saved-collections-back-to-search>
    </div>
    <div class="saved-collections__content">
      <div class="saved-collections__content-table">
        <saved-collections-list
          :rows="table.rows"
          :columns="table.columns"
          :loading="table.loading"
          :total-elements="pagination.totalElements"
          :fetch-saved-collections="fetchSavedCollections"
          :unpin-filter-click="fetchAllSavedCollections"
          :pin-filter-click="fetchOnlyPinnedSavedCollections"
          :on-change-sort-table-column-visibility="onChangeSortTableColumnVisibility"
          :on-change-sort-table-direction="onChangeSortTableDirection"
          :is-pinned-filter-active="isPinnedFilterActive"
          @send-to-search="handleSendToSearch"
        ></saved-collections-list>
      </div>
      <div class="saved-collections__content-pagination q-pb-lg">
        <mi-pagination
          v-if="!!table.rows.length"
          :model-value="pagination.currentPage"
          :max="pagination.totalPages"
          :max-pages="pagination.maxNavPage"
          size="14px"
          boundary-links
          direction-links
          boundary-numbers
          ellipses
          @update:model-value="handlePaginationChange"
        ></mi-pagination>
      </div>
      <send-to-search-confirmation
        v-model:confirmation-modal="showConfirmationDialog"
        :selected-row="selectedRowForSearch || {}"
        :product-models="productModels"
        @confirm="handleConfirmSendToSearch"
      >
      </send-to-search-confirmation>
      <mi-inner-loading :showing="isLoading">
        <mi-spinner size="70px"></mi-spinner>
        <h6 class="q-mt-lg q-mb-none">Fixing selections before sending... </h6>
      </mi-inner-loading>
      <change-pm-confirmation
        :change-pm-modal="changeModal"
        :product-models="productModels"
        :handle-change-pm-modal="handleChangePmModal"
        :selected-user-export="selectedUserExport"
        :success-send-to-search-modal-user-export="successSendToSearchModalUserExport"
      >
      </change-pm-confirmation>
    </div>
  </div>
</template>

<script>
  import { onMounted, ref, watch, provide } from 'vue'
  import { usePagination } from '@/composables/usePagination'
  import { useTable } from '@/composables/useTable'
  import { useRouter } from 'vue-router'
  import { useStore } from 'vuex'
  import { SEARCH_ROUTE } from '@/router/routeNames'
  import { getProductModels } from '@/api/rest/product-model'
  import SendToSearchConfirmation from '@/components/saved-collections/dialogs/SendToSearchConfirmation.vue'
  import notify from '@/utils/notify'
  import ChangePmConfirmation from '@/components/saved-collections/dialogs/ChangePmConfirmation.vue'
  // import { UPDATE_SEARCH_PRODUCT_MODELS_LIST } from '@/store/modules/product-model/mutationTypes'
  import SavedCollectionsBackToSearch from './SavedCollectionsBackToSearch.vue'
  import SavedCollectionsList from './SavedCollectionsList.vue'
  import { savedCollectionsColumns, SEARCH_STRING_PROVIDER } from './utils'
  import { getSavedCollectionsList, loadSavedCollection } from '../../api/rest/saved-collections'

  export default {
    name: 'SavedTablesContainer',
    components: { ChangePmConfirmation, SavedCollectionsList, SavedCollectionsBackToSearch, SendToSearchConfirmation },
    setup() {
      const router = useRouter()
      const store = useStore()
      const isLoading = ref(false)
      const showConfirmationDialog = ref(false)
      const selectedRowForSearch = ref(null)
      const { pagination, handleChange: handlePaginationChange } = usePagination()
      const {
        table, callbacks: {
          fetchTableData,
          onChangeSortTableDirection,
          onChangeSortTableColumnVisibility
        }
      } = useTable(savedCollectionsColumns)

      const isPinnedFilterActive = ref(false)

      const savedCollectionsSearchString = ref('')

      const productModels = ref([])
      const changeModal = ref(false)
      const selectedUserExport = ref('')
      const userExports = ref({ userExports: { bom: [], basic: [] } })
      const completeListWithUserExports = ref({})

      const loadUserExports = async () => {
        const exports = await store.dispatch('rule-validation/fetchUserExportsByTypes')
        userExports.value = { userExports: exports }
      }

      const handleChangePmModal = () => {
        changeModal.value = !changeModal.value
      }

      provide(SEARCH_STRING_PROVIDER, savedCollectionsSearchString)

      const handleRedirectToSearchPage = () => {
        router.push({ name: SEARCH_ROUTE.name })
      }

      const buildUrlSearchParams = isPinned => {
        const params = new URLSearchParams()

        params.append('page', pagination.currentPage - 1)
        params.append('pageSize', pagination.pageSize)
        params.append('sort',
                      table.sort?.sortBy
                        ? `${ table.sort?.sortBy },${ table.sort?.sortDirection }`
                        : 'name,asc')
        if (isPinned) params.append('isPinned', isPinned)
        savedCollectionsSearchString.value && params.append('search', savedCollectionsSearchString.value)
        return params.toString()
      }

      const fetchSavedCollections = async ({ isPinned = false } = {}) => {
        const params = buildUrlSearchParams(isPinned)
        const fetchSavedCollectionsPromise = () => getSavedCollectionsList(params)
        const data = await fetchTableData(fetchSavedCollectionsPromise)
        pagination.totalPages = data?.totalPages || 1
        pagination.totalElements = data?.totalElements || 0
      }

      watch(
        () => [pagination.currentPage, table.sort, savedCollectionsSearchString.value],
        ([, newSort, newSearch], [, oldSort, oldSearch]) => {
          if (newSort !== oldSort || newSearch !== oldSearch) pagination.currentPage = 1
          fetchSavedCollections({ isPinned: isPinnedFilterActive.value })
        }
      )

      const fetchOnlyPinnedSavedCollections = () => {
        isPinnedFilterActive.value = true
        fetchSavedCollections({ isPinned: true })
      }

      const fetchAllSavedCollections = () => {
        isPinnedFilterActive.value = false
        fetchSavedCollections({ isPinned: false })
      }

      const isCollectionPmAvailable = row => {
        const { basic = [], bom = [] } = completeListWithUserExports.value
        return [...basic, ...bom, ...productModels.value].some(item => item.id === row.productModelIdLastEdited)
      }

      const handleSendToSearch = row => {
        if (row.isUserExport === true && !isCollectionPmAvailable(row)) {
          changeModal.value = true
          selectedUserExport.value = {
            id: row.id,
            name: row.name,
            options: row.options,
            components: row.components,
            planningPeriod: {
              from: row.planningPeriodRange.from,
              to: row.planningPeriodRange.to
            },
            pmEncodedBusinessName: row.pmEncodedBusinessName,
            businessName: row.businessName,
            productModelId: row.productModelIdLastEdited
          }
          return
        }
        selectedRowForSearch.value = row
        showConfirmationDialog.value = true
      }

      const successSendToSearchModalUserExport = () => {
        changeModal.value = !changeModal.value
        // Set the selected row for search and open confirmation modal
        selectedRowForSearch.value = selectedUserExport.value
        showConfirmationDialog.value = true
      }

      const handleConfirmSendToSearch = async row => {
        try {
          isLoading.value = true

          store.commit('search/CLEAR_SELECTED_ELEMENTS', 'options')
          store.commit('search/CLEAR_SELECTED_ELEMENTS', 'components')

          await loadSavedCollection(row.id)

          await router.push({ name: SEARCH_ROUTE.name })
        }
        catch (error) {
          notify({
            title: 'Error',
            content: 'Collection was not able to load',
            type: 'negative',
            progress: true
          })
        }
        finally {
          isLoading.value = false
        }
      }

      const loadProductModels = async () => {
        productModels.value = await getProductModels()
        await loadUserExports()
        completeListWithUserExports.value = { ...userExports.value.userExports, ...productModels.value }
      }

      onMounted(async () => {
        await fetchSavedCollections({ isPinned: false })
        await loadProductModels()
      })

      return {
        handleRedirectToSearchPage,
        table,
        pagination,
        handlePaginationChange,
        buildUrlSearchParams,
        fetchSavedCollections,
        onChangeSortTableColumnVisibility,
        onChangeSortTableDirection,
        savedCollectionsSearchString,
        handleSendToSearch,
        handleConfirmSendToSearch,
        showConfirmationDialog,
        selectedRowForSearch,
        isLoading,
        productModels,
        handleChangePmModal,
        changeModal,
        selectedUserExport,
        successSendToSearchModalUserExport,
        fetchOnlyPinnedSavedCollections,
        fetchAllSavedCollections,
        isPinnedFilterActive
      }
    }
  }
</script>

<style lang="scss" scoped>
  .export-error-dialog {
    p {
      line-height: 24px;
    }
  }

  .saved-collections {
    max-width: 100vw;
    height: calc(100vh - 60px);
    background-color: $mi-anthracite-50;

    &__header {
      background-color: $mi-white;
      height: 48px;
      display: flex;
      padding-left: 24px;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }

    &__filter-wrapper {
      height: 3.75rem;
      width: 100%;
      justify-content: flex-start;
      align-items: center;
      padding: .5rem 1rem;
      box-shadow: 0 4px 8px rgba(0, 0, 0, .24);
      position: relative;
    }

    &__content {
      background-color: $mi-white;
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 24px;
      gap: 24px;

      &-banner {
        max-width: 700px;
      }

      &-table {
        width: 100%;
      }

      &-pagination {
        display: flex;
        justify-content: center;
      }
    }
  }
</style>
